import { countBy, sortBy, uniq } from "lodash";
import isNullish from "utils/isNullish";
import { uniqueNumberedName } from "utils/nameUniqueness";
import { CORE_COLUMN_NAMES } from "utils/shreddingTemplate/coreColumns";

const MIN_GROUP_MEMBERS = 2;

export const getColumnKey = (column) => column.id ?? column.key;

export const getGroupKey = (group) => group.id ?? group.key;

export const columnGroupKey = (column) =>
  column.groupId ?? column.groupKey ?? null;

const withPositions = (items) =>
  items.map((item, position) => ({ ...item, position }));

export const dissolveUndersizedGroups = (draft) => {
  const columns = draft.columns ?? [];
  const members = countBy(
    columns.filter((column) => columnGroupKey(column) != null),
    columnGroupKey
  );
  const isKept = (groupKey) => (members[groupKey] ?? 0) >= MIN_GROUP_MEMBERS;

  return {
    ...draft,
    groups: (draft.groups ?? []).filter((group) => isKept(getGroupKey(group))),
    columns: columns.map((column) => {
      const groupKey = columnGroupKey(column);
      return groupKey == null || isKept(groupKey)
        ? column
        : { ...column, groupId: null, groupKey: null };
    }),
  };
};

export const updateColumn = (draft, columnKey, patch) => ({
  ...draft,
  columns: (draft.columns ?? []).map((column) =>
    getColumnKey(column) === columnKey ? { ...column, ...patch } : column
  ),
});

export const removeColumn = (draft, columnKey) =>
  dissolveUndersizedGroups({
    ...draft,
    columns: withPositions(
      (draft.columns ?? []).filter(
        (column) => getColumnKey(column) !== columnKey
      )
    ),
  });

export const MIN_COLUMN_WIDTH = 64;

const CORE_WIDTH_FIELDS = {
  page: "pageWidth",
  para: "paraWidth",
  content: "contentWidth",
};

const CORE_HIDDEN_FIELDS = {
  page: "pageHidden",
  para: "paraHidden",
};

export const isHideableCoreColumn = (previewColumnId) =>
  Boolean(CORE_HIDDEN_FIELDS[previewColumnId]);

export const toggleCoreColumnHidden = (draft, previewColumnId) => {
  const hiddenField = CORE_HIDDEN_FIELDS[previewColumnId];
  if (!hiddenField) {
    return draft;
  }
  return { ...draft, [hiddenField]: !draft[hiddenField] };
};

const EXTRA_COLUMN_ID_PREFIX = "column-";

export const toPreviewColumnId = (column) =>
  `${EXTRA_COLUMN_ID_PREFIX}${getColumnKey(column)}`;

export const clampColumnWidth = (px) =>
  Math.max(MIN_COLUMN_WIDTH, Math.round(Number(px) || 0));

export const setColumnWidth = (draft, previewColumnId, px) => {
  if (!Number.isFinite(Number(px))) {
    return draft;
  }
  const width = clampColumnWidth(px);
  const coreField = CORE_WIDTH_FIELDS[previewColumnId];
  if (coreField) {
    return { ...draft, [coreField]: width };
  }
  return {
    ...draft,
    columns: (draft.columns ?? []).map((column) =>
      toPreviewColumnId(column) === previewColumnId
        ? { ...column, width }
        : column
    ),
  };
};

const rankIn = (order, key) => {
  const index = order.indexOf(key);
  return index < 0 ? Infinity : index;
};

const orderGroupsByColumns = (groups, columns) => {
  const appearance = uniq(columns.map(columnGroupKey));
  return withPositions(
    sortBy(groups, (group) => rankIn(appearance, getGroupKey(group)))
  );
};

export const reorderColumns = (draft, previewColumnIds) => {
  const columns = withPositions(
    sortBy(draft.columns ?? [], (column) =>
      rankIn(previewColumnIds, toPreviewColumnId(column))
    )
  );
  return {
    ...draft,
    columns,
    groups: orderGroupsByColumns(draft.groups ?? [], columns),
  };
};

const COPY_PREFIX = "Copy of ";

const membershipOf = (group) =>
  isNullish(group.id)
    ? { groupId: null, groupKey: group.key }
    : { groupId: group.id, groupKey: null };

const ungroupedMembership = { groupId: null, groupKey: null };

const isMemberOf = (groupKey) => (column) =>
  columnGroupKey(column) === groupKey;

const withSettledGroups = (draft) => {
  const settled = dissolveUndersizedGroups(draft);
  const columns = withPositions(settled.columns);
  return {
    ...settled,
    columns,
    groups: orderGroupsByColumns(settled.groups, columns),
  };
};

export const pullTogether = (columns, columnKeys) => {
  const isPulled = (column) => columnKeys.includes(getColumnKey(column));
  const firstAt = columns.findIndex(isPulled);
  if (firstAt < 0) {
    return columns;
  }
  const pulled = columns.filter(isPulled);
  const rest = columns.filter((column) => !isPulled(column));
  return [...rest.slice(0, firstAt), ...pulled, ...rest.slice(firstAt)];
};

export const findGroup = (draft, groupKey) =>
  (draft.groups ?? []).find((group) => getGroupKey(group) === groupKey) ?? null;

export const getGroupMembers = (draft, groupKey) =>
  (draft.columns ?? []).filter(isMemberOf(groupKey));

export const groupColumns = (
  draft,
  { key, columnKeys, name, color, renamable, repeatable }
) => {
  const group = {
    id: null,
    key,
    name: name.trim(),
    color,
    renamable: Boolean(renamable),
    repeatable: Boolean(repeatable),
  };
  const columns = (draft.columns ?? []).map((column) =>
    columnKeys.includes(getColumnKey(column))
      ? { ...column, ...membershipOf(group), pinned: false }
      : column
  );
  return withSettledGroups({
    ...draft,
    groups: [...(draft.groups ?? []), group],
    columns: pullTogether(columns, columnKeys),
  });
};

export const updateGroup = (draft, groupKey, patch) => ({
  ...draft,
  groups: (draft.groups ?? []).map((group) =>
    getGroupKey(group) === groupKey ? { ...group, ...patch } : group
  ),
});

export const ungroupColumns = (draft, groupKey) =>
  withSettledGroups({
    ...draft,
    groups: (draft.groups ?? []).filter(
      (group) => getGroupKey(group) !== groupKey
    ),
    columns: (draft.columns ?? []).map((column) =>
      isMemberOf(groupKey)(column)
        ? { ...column, ...ungroupedMembership }
        : column
    ),
  });

export const removeColumns = (draft, columnKeys) =>
  withSettledGroups({
    ...draft,
    columns: (draft.columns ?? []).filter(
      (column) => !columnKeys.includes(getColumnKey(column))
    ),
  });

export const deleteGroup = (draft, groupKey) =>
  removeColumns(draft, getGroupMembers(draft, groupKey).map(getColumnKey));

const uniqueCopyName = (name, takenNames) =>
  uniqueNumberedName(`${COPY_PREFIX}${name}`, takenNames);

const takenColumnNames = (columns) => [
  ...CORE_COLUMN_NAMES,
  ...columns.map((column) => column.name),
];

const copyColumn = (source, key, name) => ({
  ...source,
  id: null,
  key,
  name,
  values: (source.values ?? []).map(({ value, color }) => ({
    value,
    color,
  })),
});

const copyColumns = (members, groupKey, existingColumns) => {
  const takenNames = takenColumnNames(existingColumns);
  return members.map((member, index) => {
    const name = uniqueCopyName(member.name, takenNames);
    takenNames.push(name);
    return {
      ...copyColumn(member, `${groupKey}-${index}`, name),
      groupId: null,
      groupKey,
    };
  });
};

export const duplicateColumn = (draft, columnKey, copyKey) => {
  const columns = draft.columns ?? [];
  const sourceAt = columns.findIndex(
    (column) => getColumnKey(column) === columnKey
  );
  if (sourceAt < 0) {
    return draft;
  }
  const source = columns[sourceAt];
  const name = uniqueCopyName(source.name, takenColumnNames(columns));
  return withSettledGroups({
    ...draft,
    columns: [
      ...columns.slice(0, sourceAt + 1),
      copyColumn(source, copyKey, name),
      ...columns.slice(sourceAt + 1),
    ],
  });
};

export const duplicateGroup = (draft, groupKey, copyKey) => {
  const source = findGroup(draft, groupKey);
  const columns = draft.columns ?? [];
  const members = getGroupMembers(draft, groupKey);
  if (!source || members.length === 0) {
    return draft;
  }
  const copies = copyColumns(members, copyKey, columns);
  const insertAt = columns.findLastIndex(isMemberOf(groupKey)) + 1;
  return withSettledGroups({
    ...draft,
    groups: [
      ...(draft.groups ?? []),
      {
        ...source,
        id: null,
        key: copyKey,
        name: uniqueCopyName(
          source.name,
          (draft.groups ?? []).map((group) => group.name)
        ),
      },
    ],
    columns: [
      ...columns.slice(0, insertAt),
      ...copies,
      ...columns.slice(insertAt),
    ],
  });
};

export const addColumn = (draft, column, intoGroupKey = null) => {
  const columns = draft.columns ?? [];
  const group = isNullish(intoGroupKey) ? null : findGroup(draft, intoGroupKey);
  if (!group) {
    return {
      ...draft,
      columns: withPositions([
        ...columns,
        { ...column, id: null, ...ungroupedMembership },
      ]),
    };
  }
  const insertAt = columns.findLastIndex(isMemberOf(intoGroupKey)) + 1;
  return withSettledGroups({
    ...draft,
    columns: [
      ...columns.slice(0, insertAt),
      { ...column, id: null, ...membershipOf(group), pinned: false },
      ...columns.slice(insertAt),
    ],
  });
};
